<script setup lang="ts">
import type { InputHTMLAttributes } from 'vue';

import { useVuelidate } from '@vuelidate/core';
import {
  required as requiredValidator,
  minLength as minLengthValidator,
  maxLength as maxLengthValidator,
} from '@vuelidate/validators';
import { computed, ref, nextTick, onBeforeUpdate, onMounted, useAttrs, useId, useSlots, watch } from 'vue';

import { useFloatingPosition } from '@/shared/lib/composables';
import { foldCaseAndYo } from '@/shared/lib/formatters';
import { AppSelectOption } from '@/shared/ui/select';
import { AppSpinner } from '@/shared/ui/spinner';

import type { InputMask } from './input-masks';
import type { InputSuggestion } from './types';

import { formatInputValue, getFormattedCaretPosition, getMaskedCaretPosition } from './input-masks';

defineOptions({
  inheritAttrs: false,
});

type InputValue = string | number | null;

interface Props {
  type?: InputHTMLAttributes['type'];
  inputmode?: InputHTMLAttributes['inputmode'];
  label?: string;
  modelValue?: InputValue;
  mask?: InputMask;
  /** Своё форматирование вместо маски и регистра; курсор остаётся у введённого символа. */
  format?: (value: string) => string;
  placeholder?: string;
  firstLetterUpperCase?: boolean;
  upperCase?: boolean;
  lowerCase?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  loading?: boolean;
  variantIsError?: boolean;
  required?: boolean;
  requiredLabel?: string;
  maxLength?: number | boolean;
  minLength?: number;
  autofocus?: boolean;
  lazy?: boolean;
  /** Подсказки превращают поле в combobox: список под полем, выбор мышью и клавиатурой. */
  suggestions?: (InputSuggestion | string)[];
  /** После ввода первый вариант подсвечен, Enter выбирает его сразу. */
  autoHighlight?: boolean;
  /** Tab выбирает подсвеченный вариант и переводит фокус дальше. */
  selectOnTab?: boolean;
  /** Остаток варианта, который продолжает ввод, виден серым в поле; → в конце текста принимает его. */
  inlineComplete?: boolean;
  size?: 'sm' | 'md';
}

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
  type: 'text',
  inputmode: undefined,
  modelValue: '',
  mask: false,
  format: undefined,
  placeholder: '',
  firstLetterUpperCase: false,
  upperCase: false,
  lowerCase: false,
  disabled: false,
  readonly: false,
  loading: false,
  variantIsError: false,
  required: false,
  requiredLabel: '*',
  maxLength: false,
  minLength: 0,
  autofocus: false,
  lazy: false,
  suggestions: undefined,
  autoHighlight: false,
  selectOnTab: false,
  inlineComplete: false,
  size: 'md',
});

interface Emits {
  (e: 'update:modelValue', value: string | number): void;
  (e: 'suggestion-selected', suggestion: InputSuggestion | string): void;
  /** Список подсказок долистан до конца: можно догрузить следующие. */
  (e: 'suggestions-end'): void;
  (e: 'focus', event: FocusEvent): void;
  (e: 'blur', event: FocusEvent): void;
  (e: 'keydown', event: KeyboardEvent): void;
}

const emit = defineEmits<Emits>();

const attrs = useAttrs();
const slots = useSlots();
const generatedInputId = useId();
const inputRef = ref<HTMLInputElement | null>(null);
const isFocused = ref(false);
const inputId = computed(() => {
  const providedId = attrs.id;

  return typeof providedId === 'string' && providedId.trim() !== '' ? providedId : generatedInputId;
});
const inputAttrs = computed<Record<string, unknown>>(() =>
  Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== 'class' && key !== 'id' && key !== 'style')),
);

const formatValue = (value: string): string =>
  props.format?.(value) ??
  formatInputValue(value, {
    mask: props.mask,
    firstLetterUpperCase: props.firstLetterUpperCase,
    upperCase: props.upperCase,
    lowerCase: props.lowerCase,
    maxLength: props.maxLength,
  });

const internalValue = computed({
  get() {
    return String(props.modelValue ?? '');
  },
  set(newValue) {
    emit('update:modelValue', formatValue(newValue));
  },
});

const inputType = computed(() => {
  if (props.mask === 'number') return 'text';
  return props.type ?? 'text';
});

const inputMode = computed(() => {
  if (props.mask === 'money') return 'decimal';
  if (props.mask === 'number') return 'numeric';

  return props.inputmode;
});

const computedPlaceholder = computed(() => {
  if (props.mask === 'date' && !props.placeholder) {
    return 'дд.мм.гггг';
  }

  return props.placeholder;
});

const validationRules = computed(() => {
  const internalValueRules: Record<string, unknown> = {};

  if (props.required) {
    internalValueRules.required = requiredValidator;
  }

  if (props.minLength > 0) {
    internalValueRules.minLength = minLengthValidator(props.minLength);
  }

  if (props.maxLength && typeof props.maxLength === 'number') {
    internalValueRules.maxLength = maxLengthValidator(props.maxLength);
  }

  return {
    internalValue: internalValueRules,
  };
});

const vuelidate = useVuelidate(validationRules, { internalValue });

const showErrorBorder = computed(() => props.variantIsError || vuelidate.value.$error);

// Поле встряхивается, когда ошибка появилась не под пальцами: после отправки формы или ухода из поля.
// ponytail: повторная отправка с той же ошибкой не встряхивает — поле не знает о попытках отправки.
const isShaking = ref(false);
watch(showErrorBorder, (hasError, hadError) => {
  if (hasError && !hadError && !isFocused.value) {
    isShaking.value = true;
  }
});

const getSuggestionLabel = (suggestion: InputSuggestion | string): string => {
  return typeof suggestion === 'object' ? suggestion.label : suggestion;
};

const listId = useId();
const isOpen = ref(false);
const activeIndex = ref(-1);
const suggestionItems = computed(() => props.suggestions ?? []);
// Слоты не реактивны: наличие footer перечитывается перед каждым обновлением от родителя.
const hasFooter = ref(slots['suggestions-footer'] !== undefined);
onBeforeUpdate(() => {
  hasFooter.value = slots['suggestions-footer'] !== undefined;
});
const expanded = computed(
  () => isOpen.value && (suggestionItems.value.length > 0 || hasFooter.value) && !props.disabled && !props.readonly,
);
const { reference, floating, floatingStyles, availableHeight, isPositioned } = useFloatingPosition({
  open: expanded,
  widthBehaviour: 'match',
  maxHeight: 240,
});
const setReference = (element: unknown) => {
  reference.value = element as HTMLElement | null;
};
const setFloating = (element: unknown) => {
  floating.value = element as HTMLElement | null;
};
const optionId = (index: number): string => `${listId}-${index}`;
const comboboxAttrs = computed<Record<string, unknown>>(() =>
  props.suggestions === undefined
    ? {}
    : {
        role: 'combobox',
        autocomplete: attrs.autocomplete ?? 'off',
        'aria-autocomplete': 'list',
        'aria-expanded': expanded.value,
        'aria-controls': listId,
        'aria-activedescendant': expanded.value && activeIndex.value >= 0 ? optionId(activeIndex.value) : undefined,
      },
);

// Автоподсветка только после ввода: на фокусе сохранённое значение не должно подменяться по Tab.
const hasTypedSinceFocus = ref(false);
const resetActive = (text: string = internalValue.value) => {
  activeIndex.value =
    props.autoHighlight && hasTypedSinceFocus.value && text.trim() !== '' && suggestionItems.value.length > 0 ? 0 : -1;
};

/** Подсвеченный вариант, иначе первый, если он продолжает набранное без учёта регистра и ё. */
const inlineSuggestion = computed(() => {
  if (!props.inlineComplete || !expanded.value || !hasTypedSinceFocus.value) return undefined;

  const suggestion = suggestionItems.value[activeIndex.value] ?? suggestionItems.value[0];
  const typed = internalValue.value;
  const label = suggestion === undefined ? '' : getSuggestionLabel(suggestion);

  return typed !== '' && label.length > typed.length && foldCaseAndYo(label).startsWith(foldCaseAndYo(typed))
    ? suggestion
    : undefined;
});
const inlineRest = computed(() =>
  inlineSuggestion.value === undefined
    ? ''
    : getSuggestionLabel(inlineSuggestion.value).slice(internalValue.value.length),
);

watch(
  () => props.suggestions,
  (next, previous) => {
    const active = previous?.[activeIndex.value];
    const kept = next?.[activeIndex.value];

    // Список дорос снизу: подсветка остаётся на месте, стрелки не прыгают в начало.
    if (active !== undefined && kept !== undefined && getSuggestionLabel(active) === getSuggestionLabel(kept)) return;
    resetActive();
  },
);

const syncInputElementValue = (target: HTMLInputElement): string => {
  const rawValue = target.value;
  const rawCaretPosition = target.selectionStart;
  const formattedValue = formatValue(rawValue);

  if (formattedValue !== rawValue) {
    target.value = formattedValue;
  }

  if (rawCaretPosition !== null && (props.mask === 'number' || props.mask === 'money')) {
    const caretPosition = getMaskedCaretPosition(props.mask, rawValue, formattedValue, rawCaretPosition);
    target.setSelectionRange(caretPosition, caretPosition);
  } else if (rawCaretPosition !== null && props.format && formattedValue !== rawValue) {
    const caretPosition = getFormattedCaretPosition(props.format, rawValue, formattedValue, rawCaretPosition);
    target.setSelectionRange(caretPosition, caretPosition);
  }

  return formattedValue;
};

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = syncInputElementValue(target);

  if (!props.lazy) {
    internalValue.value = value;
  }

  isOpen.value = true;
  hasTypedSinceFocus.value = true;
  resetActive(value);
};

const handleChange = (event: Event) => {
  if (props.lazy) {
    const target = event.target as HTMLInputElement;
    internalValue.value = syncInputElementValue(target);
  }
};

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true;
  isOpen.value = true;
  hasTypedSinceFocus.value = false;
  activeIndex.value = -1;
  emit('focus', event);
};

const handleBlur = (event: FocusEvent) => {
  isFocused.value = false;
  isOpen.value = false;
  vuelidate.value.$touch();
  emit('blur', event);
};

const handleSuggestionSelect = (suggestion: InputSuggestion | string) => {
  internalValue.value = getSuggestionLabel(suggestion);
  isOpen.value = false;
  emit('suggestion-selected', suggestion);
};

/** Запас до конца списка, с которого просим следующие подсказки: примерно одна строка. */
const SUGGESTIONS_END_OFFSET_PX = 48;

const handleSuggestionsScroll = (event: Event) => {
  const list = event.target as HTMLElement;

  if (list.scrollHeight - list.scrollTop - list.clientHeight <= SUGGESTIONS_END_OFFSET_PX) {
    emit('suggestions-end');
  }
};

const closeSuggestions = () => {
  isOpen.value = false;
};

const moveActive = (step: 1 | -1) => {
  const count = suggestionItems.value.length;
  isOpen.value = true;
  activeIndex.value = activeIndex.value < 0 ? (step === 1 ? 0 : count - 1) : (activeIndex.value + step + count) % count;
  document.getElementById(optionId(activeIndex.value))?.scrollIntoView({ block: 'nearest' });
};

/** Клавиатура списка подсказок; true — клавиша обработана списком. */
const handleSuggestionKeydown = (event: KeyboardEvent): boolean => {
  if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && suggestionItems.value.length > 0) {
    event.preventDefault();
    moveActive(event.key === 'ArrowDown' ? 1 : -1);
    return true;
  }

  if (!expanded.value) {
    return false;
  }

  const input = event.target as HTMLInputElement;

  if (
    event.key === 'ArrowRight' &&
    inlineSuggestion.value !== undefined &&
    input.selectionStart === input.value.length &&
    input.selectionEnd === input.value.length
  ) {
    event.preventDefault();
    handleSuggestionSelect(inlineSuggestion.value);
    return true;
  }

  if (event.key === 'Escape') {
    // Список закрывается без модалки вокруг поля.
    event.stopPropagation();
    isOpen.value = false;
    return true;
  }

  const active = suggestionItems.value[activeIndex.value];

  if (event.key === 'Tab') {
    if (props.selectOnTab && active !== undefined) {
      handleSuggestionSelect(active);
    }
    isOpen.value = false;
    return false;
  }

  if (event.key === 'Enter' && active !== undefined) {
    event.preventDefault();
    handleSuggestionSelect(active);
    return true;
  }

  return false;
};

const handleKeydown = (event: KeyboardEvent) => {
  if (props.suggestions !== undefined && handleSuggestionKeydown(event)) {
    return;
  }

  // Для маски даты блокируем ввод недопустимых символов
  if (props.mask === 'date') {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value;

    // Разрешаем служебные клавиши
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'PageUp',
      'PageDown',
    ];

    if (allowedKeys.includes(event.key)) {
      return; // Разрешаем служебные клавиши
    }

    // Разрешаем Ctrl/Cmd комбинации (копировать, вставить и т.д.)
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    // Блокируем ввод, если достигнута максимальная длина
    if (currentValue.length >= 10) {
      event.preventDefault();
      return;
    }

    // Разрешаем только цифры и точки
    if (!/[0-9.]/.test(event.key)) {
      event.preventDefault();
      return;
    }
  }

  // Наружу событие уходит после внутренней обработки маски: потребитель
  // (сканер) видит только то, что маска пропустила.
  emit('keydown', event);
};

onMounted(() => {
  if (props.autofocus) {
    void nextTick(() => {
      inputRef.value?.focus();
    });
  }
});

/** Фокус снаружи — для полей сканера: родителю не нужно знать разметку поля. */
defineExpose({ focus: (): void => inputRef.value?.focus() });
</script>

<template>
  <div
    :class="['flex flex-col gap-1.5', $attrs.class]"
    :style="$attrs.style"
  >
    <label
      v-if="label || $slots.label"
      :id="`${inputId}-label`"
      :for="inputId"
      class="text-sm font-medium text-(--base-popup-text-color)"
    >
      <slot name="label">{{ label }}</slot>
    </label>

    <div
      :ref="setReference"
      class="relative"
      :class="{ 'motion-safe:animate-shake': isShaking }"
      @animationend.self="isShaking = false"
    >
      <input
        :id="inputId"
        ref="inputRef"
        v-bind="{ ...inputAttrs, ...comboboxAttrs }"
        :value="internalValue"
        class="input-class peer"
        :placeholder="computedPlaceholder"
        :class="[size === 'sm' ? 'input-size-sm' : 'input-size-md', { 'border-rose-400!': showErrorBorder }]"
        :disabled="disabled"
        :readonly="readonly"
        :type="inputType"
        :inputmode="inputMode"
        :pattern="mask === 'number' ? '\\d*' : undefined"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleChange"
        @input="handleInput"
        @keydown="handleKeydown"
      />

      <!-- Дописывание поверх поля: набранное невидимо задаёт отступ, остаток серый. -->
      <div
        v-if="inlineRest"
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 flex items-center overflow-hidden border border-transparent whitespace-pre"
        :class="size === 'sm' ? 'input-size-sm' : 'input-size-md'"
      >
        <span class="invisible">{{ internalValue }}</span>
        <span class="text-(--base-secondary-text-color)">{{ inlineRest }}</span>
        <!-- Как принять остаток; на сенсорном экране клавиши нет, вариант выбирают в списке. -->
        <kbd
          class="ml-1.5 shrink-0 rounded border border-(--base-border-color) px-1 py-0.5 font-sans text-[10px] leading-none text-(--base-secondary-text-color) pointer-coarse:hidden"
          >→</kbd
        >
      </div>

      <!-- Обязательная метка -->
      <div
        v-if="required && requiredLabel"
        class="text-gray-primary pointer-events-none absolute inset-y-0 right-3 hidden h-full items-center justify-center text-sm peer-placeholder-shown:flex"
      >
        {{ requiredLabel }}
      </div>

      <!-- Лоадер -->
      <div
        v-if="loading"
        class="absolute inset-y-0 right-3"
      >
        <div class="flex h-full w-full items-center justify-center">
          <AppSpinner class="h-[20px] w-[20px]" />
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="expanded"
        :ref="setFloating"
        data-modal-ignore
        class="scrollbar-sm z-999999 flex flex-col gap-1 overflow-y-auto rounded-xl border border-(--base-popup-border-color) bg-(--base-popup-bg) p-2"
        :style="{
          ...floatingStyles,
          maxHeight: `${availableHeight ?? 240}px`,
          visibility: isPositioned ? undefined : 'hidden',
        }"
        @pointerdown.prevent
        @scroll.passive="handleSuggestionsScroll"
      >
        <div
          :id="listId"
          role="listbox"
          :aria-labelledby="label || $slots.label ? `${inputId}-label` : undefined"
          class="flex flex-col gap-1"
        >
          <AppSelectOption
            v-for="(suggestion, index) in suggestionItems"
            :id="optionId(index)"
            :key="getSuggestionLabel(suggestion)"
            :option="{ label: getSuggestionLabel(suggestion) }"
            :is-active="activeIndex === index"
            :is-selected="false"
            :option-classes="size === 'sm' ? 'min-h-9 gap-x-1.5 p-2 text-sm' : 'min-h-11 gap-x-1.5 p-2 text-base'"
            @activate="activeIndex = index"
            @select="handleSuggestionSelect(suggestion)"
          >
            <slot
              name="suggestion"
              :suggestion="suggestion"
            >
              {{ getSuggestionLabel(suggestion) }}
            </slot>
          </AppSelectOption>
        </div>
        <!-- Строка-действие под вариантами: стрелки и Enter до неё не доходят, клик обрабатывает потребитель. -->
        <slot
          name="suggestions-footer"
          :close="closeSuggestions"
        />
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
@reference "../../../tailwind.css";

.input-class {
  @apply text-black-primary w-full rounded-[6px] border border-[#CBD3E4] bg-white placeholder-[#AEAEAE] transition-all duration-200 ease-in-out outline-none focus:border-[#AEAEAE] focus:ring-0 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50;

  @apply dark:border-(--base-popup-border-color) dark:bg-[#283046] dark:text-(--base-popup-text-color) dark:placeholder-(--base-popup-secondary-color) dark:focus:border-[#7367f0];
}

.input-size-md {
  @apply h-[42px] px-3;
}

.input-size-sm {
  @apply h-9 px-2.5 text-sm;
}
</style>
